import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Link,
    styled
} from '@mui/material';

interface PersonalInfoProps {
    initialData?: {
        nombre: string;
        apellido: string;
        cedula: string;
        correo: string;
        telefono: string;
    };
    onSave?: (data: any) => void;
}

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(0.75),
        backgroundColor: theme.palette.background.paper,
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '1px',
        },
    },
    '& .MuiInputLabel-root': {
        transform: 'translate(14px, -9px) scale(0.75)',
    },
    '& .MuiInputBase-input': {
        padding: '15px 14px',
    }
}));

const SaveButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1.5),
    textTransform: 'none',
    fontWeight: 500,
    backgroundColor: '#1e1e1e',
    '&:hover': {
        backgroundColor: '#000000',
    }
}));

const PersonalInfoForm: React.FC<PersonalInfoProps> = ({
    initialData = {
        nombre: 'John',
        apellido: 'Doe',
        cedula: 'V - 012345678',
        correo: 'JohnDoe@gmail.com',
        telefono: '0412 - 1234567'
    },
    onSave = (data) => console.log('Datos guardados:', data)
}) => {
    const [formData, setFormData] = useState(initialData);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                p: 4,
                border: '1px solid #eaeaea',
                maxWidth: '100%'
            }}
        >
            <Box component="form" onSubmit={handleSubmit}>
                <Typography variant="h5" component="h1" fontWeight={500} gutterBottom>
                    Información personal
                </Typography>

                <Link
                    href="#"
                    underline="none"
                    color="primary"
                    sx={{
                        display: 'block',
                        mb: 3,
                        fontSize: '0.875rem'
                    }}
                >
                    Revisa o actualiza tus datos actuales
                </Link>

                <Box sx={{ mt: 4 }}>
                    <Typography variant="body1" fontWeight={500} gutterBottom>
                        Nombre
                    </Typography>
                    <StyledTextField
                        fullWidth
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        variant="outlined"
                        inputProps={{ 'aria-label': 'nombre' }}
                    />

                    <Typography variant="body1" fontWeight={500} gutterBottom>
                        Apellido
                    </Typography>
                    <StyledTextField
                        fullWidth
                        name="apellido"
                        value={formData.apellido}
                        onChange={handleChange}
                        variant="outlined"
                        inputProps={{ 'aria-label': 'apellido' }}
                    />

                    <Typography variant="body1" fontWeight={500} gutterBottom>
                        Cédula
                    </Typography>
                    <StyledTextField
                        fullWidth
                        name="cedula"
                        value={formData.cedula}
                        onChange={handleChange}
                        variant="outlined"
                        inputProps={{ 'aria-label': 'cédula' }}
                    />

                    <Typography variant="body1" fontWeight={500} gutterBottom>
                        Correo
                    </Typography>
                    <StyledTextField
                        fullWidth
                        name="correo"
                        value={formData.correo}
                        onChange={handleChange}
                        variant="outlined"
                        inputProps={{ 'aria-label': 'correo' }}
                    />

                    <Typography variant="body1" fontWeight={500} gutterBottom>
                        Número telefónico
                    </Typography>
                    <StyledTextField
                        fullWidth
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        variant="outlined"
                        inputProps={{ 'aria-label': 'número telefónico' }}
                    />

                    <Box sx={{ mt: 5 }}>
                        <SaveButton
                            fullWidth
                            variant="contained"
                            color="primary"
                            type="submit"
                            size="large"
                        >
                            Guardar cambios
                        </SaveButton>
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
};

export default PersonalInfoForm;